import { useState, useMemo } from 'react';
import { GripVertical, MapPin, Search, Compass, Loader, Plus } from 'lucide-react';
import { exploreCitySearch } from '../../api/atlas';

const cleanCityName = (city) =>
  city.replace(/\s+(Prefecture|Province|Region|District|County|Metropolitan)$/i, '').trim();

const QUICK_SEARCHES = [
  { label: 'Best restaurants', query: 'best restaurants' },
  { label: 'Rooftop bars', query: 'rooftop bars' },
  { label: 'Cafés', query: 'best cafés' },
  { label: 'Spa & wellness', query: 'spa wellness' },
  { label: 'Street food', query: 'street food' },
  { label: 'Live music', query: 'live music venues' },
];

const PRICE_COLOR = {
  'Free': 'text-emerald-600 bg-emerald-50',
  '€': 'text-naples-700 bg-naples-50',
  '€€': 'text-naples-700 bg-naples-50',
  '€€€': 'text-coral-600 bg-coral-50',
  '€€€€': 'text-coral-700 bg-coral-100',
};

function placeTypeToCategory(type) {
  if (!type) return 'other';
  const t = type.toLowerCase();
  if (/restaurant|cafe|café|food|bar|bistro|dining|eatery|sushi|pizza|grill/.test(t)) return 'food';
  if (/spa|wellness|yoga|massage|thermal|retreat/.test(t)) return 'relaxation';
  if (/museum|gallery|historic|temple|church|cathedral|monument|art|culture/.test(t)) return 'culture';
  if (/shop|market|mall|boutique|shopping|souvenir/.test(t)) return 'shopping';
  if (/nightclub|club|nightlife|lounge|rooftop bar|cocktail/.test(t)) return 'nightlife';
  if (/beach|park|nature|trail|hike|garden|lake|mountain|forest/.test(t)) return 'nature';
  if (/adventure|sports|outdoor|surf|dive|kayak|climb/.test(t)) return 'adventure';
  return 'other';
}

// Shape expected by handleAddActivityFromDrag / the native-DnD day drop target alike.
function buildExplorePayload(place, city) {
  return {
    title: place.name,
    description: place.description || '',
    location: city ? `${place.name}, ${cleanCityName(city)}` : place.name,
    category: placeTypeToCategory(place.type),
    duration_minutes: 90,
    estimated_cost_min: null,
    estimated_cost_max: null,
    latitude: null,
    longitude: null,
    time_of_day: null,
  };
}

// One search bar + quick-chip row, scoped to a single selected city.
function ExploreSearchBar({ cities, selectedCity, onCityChange, onSearch, loading }) {
  const [query, setQuery] = useState('');

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
      {cities.length > 1 && (
        <div className="relative">
          <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-coral-400 pointer-events-none" />
          <input
            type="text"
            list="explore-city-options"
            value={selectedCity}
            onChange={(e) => onCityChange(e.target.value)}
            placeholder="Search in..."
            className="w-full pl-8 pr-3 py-2 text-sm font-semibold border border-platinum-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-coral-300 text-charcoal-600"
          />
          <datalist id="explore-city-options">
            {cities.map((c) => (
              <option key={c} value={cleanCityName(c)} />
            ))}
          </datalist>
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); onSearch(query); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-platinum-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Best sushi, rooftop bar, spa..."
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-platinum-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-coral-300 placeholder:text-platinum-400 text-charcoal-500"
          />
        </div>
        <button
          type="submit"
          disabled={!query.trim() || loading}
          className="px-4 py-2.5 bg-coral-400 hover:bg-coral-500 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-40 flex items-center gap-1.5 flex-shrink-0"
        >
          {loading ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
          Find
        </button>
      </form>
      <div className="flex flex-wrap gap-2">
        {QUICK_SEARCHES.map(({ label, query: q }) => (
          <button
            key={label}
            type="button"
            onClick={() => { setQuery(q); onSearch(q); }}
            className="text-xs px-3 py-1.5 bg-white border border-platinum-200 rounded-full text-charcoal-500 hover:bg-coral-50 hover:border-coral-300 hover:text-coral-600 transition-colors font-medium"
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Presentational — renders results for whichever city is currently selected.
function CityExploreResults({ city, results, loading, error, onRetry, dayCount, onAddActivity }) {
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [dayChoice, setDayChoice] = useState({});
  const displayCity = cleanCityName(city);

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-16 rounded-xl bg-platinum-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-between py-2">
        <p className="text-sm text-red-500">{error}</p>
        <button onClick={onRetry} className="text-xs text-coral-500 font-semibold hover:underline">Retry</button>
      </div>
    );
  }

  if (!results) {
    return <p className="text-sm text-platinum-400 py-2">Search above to see AI picks for {displayCity}.</p>;
  }

  if (results.length === 0) {
    return <p className="text-sm text-platinum-400 text-center py-4">No suggestions found. Try a different search.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-platinum-400 flex items-center gap-1">
        <GripVertical size={10} /> Drag into a day, or pick a day and tap Add
      </p>
      {results.map((place, i) => {
        const day = dayChoice[i] ?? 1;
        return (
          <div
            key={i}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('application/json', JSON.stringify({ __type: 'explore_place', ...buildExplorePayload(place, city) }));
              setDraggingIdx(i);
            }}
            onDragEnd={() => setDraggingIdx(null)}
            className={`px-3 py-3 bg-white rounded-xl border transition-all ${
              draggingIdx === i
                ? 'opacity-40 border-coral-200'
                : 'border-platinum-100 hover:border-coral-200 hover:shadow-sm'
            }`}
          >
            <div className="flex items-start gap-2 cursor-grab active:cursor-grabbing">
              <GripVertical size={14} className="text-platinum-300 flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-bold text-charcoal-600 truncate block mb-1">{place.name}</span>
                <p className="text-xs text-platinum-600 leading-snug line-clamp-2 mb-1.5">{place.description}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {place.type && (
                    <span className="text-xs font-medium text-charcoal-400 bg-platinum-100 px-2 py-0.5 rounded-full">{place.type}</span>
                  )}
                  {place.price_range && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PRICE_COLOR[place.price_range] || 'text-platinum-500 bg-platinum-100'}`}>
                      {place.price_range}
                    </span>
                  )}
                  {place.maps_url && (
                    <a
                      href={place.maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-coral-500 hover:text-coral-600 flex items-center gap-0.5 font-medium ml-auto flex-shrink-0"
                    >
                      <MapPin size={10} /> Maps
                    </a>
                  )}
                </div>
              </div>
            </div>
            {onAddActivity && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-platinum-100">
                <select
                  value={day}
                  onChange={(e) => setDayChoice(prev => ({ ...prev, [i]: Number(e.target.value) }))}
                  className="text-xs border border-platinum-200 rounded-lg px-2 py-1 bg-white text-charcoal-500 focus:outline-none focus:ring-2 focus:ring-coral-300"
                >
                  {Array.from({ length: dayCount }, (_, d) => d + 1).map(d => (
                    <option key={d} value={d}>Day {d}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => onAddActivity(buildExplorePayload(place, city), day)}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors ml-auto"
                >
                  <Plus size={11} /> Add
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ExplorePanel({ activities, itinerary, onAddActivity }) {
  const [exploreResultsByCity, setExploreResultsByCity] = useState({});
  const [exploreLoadingByCity, setExploreLoadingByCity] = useState({});
  const [exploreErrorByCity, setExploreErrorByCity] = useState({});
  const [lastExploreQuery, setLastExploreQuery] = useState('');
  const [selectedExploreCity, setSelectedExploreCity] = useState(null);

  const dayCount = Math.max(1, itinerary?.trip_length || 1);

  // Split a destination string like "London and Paris" or "London, Paris" into parts
  const parsedDestinations = useMemo(() => {
    const dest = itinerary?.destination;
    if (!dest) return [];
    const parts = dest.split(/\s+and\s+|\s*,\s*|\s*&\s*/i).map(s => s.trim()).filter(Boolean);
    return parts.length > 1 ? parts : [dest];
  }, [itinerary]);

  // Proper city names from activities, else parsed destination parts
  const exploreCities = useMemo(() => {
    const fromActivities = [...new Set((activities || []).map(a => a.city_name).filter(Boolean))];
    if (fromActivities.length > 0) return fromActivities;
    return parsedDestinations;
  }, [activities, parsedDestinations]);

  // City field is free text (with trip cities offered as suggestions); falls back to the first trip city until typed in.
  const activeExploreCity = selectedExploreCity ?? exploreCities[0];

  // Query only hits the currently selected city, not every city in the trip.
  const runExploreSearch = async (q) => {
    if (!q.trim() || !activeExploreCity) return;
    const city = activeExploreCity;
    setLastExploreQuery(q);
    setExploreLoadingByCity(prev => ({ ...prev, [city]: true }));
    setExploreErrorByCity(prev => ({ ...prev, [city]: null }));
    try {
      const suggestions = await exploreCitySearch(city, q);
      setExploreResultsByCity(prev => ({ ...prev, [city]: suggestions }));
    } catch (e) {
      setExploreErrorByCity(prev => ({ ...prev, [city]: 'Could not load suggestions. Try again.' }));
    } finally {
      setExploreLoadingByCity(prev => ({ ...prev, [city]: false }));
    }
  };

  if (exploreCities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-columbia-50 flex items-center justify-center mb-4">
          <Compass size={28} className="text-columbia-400" />
        </div>
        <p className="text-sm font-bold text-charcoal-500 mb-1.5">Nothing here yet</p>
        <p className="text-xs text-platinum-500 leading-relaxed max-w-xs">
          Generate your itinerary to search restaurants, bars, and things to do nearby.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto bg-platinum-50">
        <div className="p-3 space-y-3">
          <ExploreSearchBar
            cities={exploreCities}
            selectedCity={activeExploreCity || ''}
            onCityChange={setSelectedExploreCity}
            onSearch={runExploreSearch}
            loading={!!exploreLoadingByCity[activeExploreCity]}
          />
          {activeExploreCity && (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="flex items-center gap-2.5 px-5 py-4 border-b border-platinum-100">
                <div className="w-8 h-8 rounded-lg bg-coral-50 flex items-center justify-center flex-shrink-0">
                  <Compass size={15} className="text-coral-500" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-charcoal-600">{cleanCityName(activeExploreCity)}</h3>
                  <p className="text-xs text-platinum-400">Discover places to visit</p>
                </div>
              </div>
              <div className="p-4">
                <CityExploreResults
                  city={activeExploreCity}
                  results={exploreResultsByCity[activeExploreCity] ?? null}
                  loading={!!exploreLoadingByCity[activeExploreCity]}
                  error={exploreErrorByCity[activeExploreCity] ?? null}
                  onRetry={() => runExploreSearch(lastExploreQuery)}
                  dayCount={dayCount}
                  onAddActivity={onAddActivity}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
